import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, LogOut, Bell, BellOff, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { requestNotificationPermission } from '../lib/pushNotifications';

interface SettingsProps {
  onClose: () => void;
}

export default function Settings({ onClose }: SettingsProps) {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const handleNotificationToggle = async () => {
    if (!notificationsEnabled) {
      const permission = await requestNotificationPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        if ('Notification' in window) {
          new Notification('Notifications Enabled!', {
            body: 'You will now receive reminders to log your meals and track your progress.',
            icon: '/icons/icon-192x192.svg'
          });
        }
        showSuccessFeedback('Notifications enabled successfully!');
      } else {
        showSuccessFeedback('Notification permission denied');
      }
    } else {
      setNotificationsEnabled(false);
      showSuccessFeedback('Notifications disabled');
    }
  };

  const showSuccessFeedback = (message: string) => {
    setFeedbackMessage(message);
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 3000);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      showSuccessFeedback('Error signing out. Please try again.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 z-50 overflow-y-auto"
    >
      <div className="min-h-screen pb-20">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-md border-b border-cyan-100 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="p-2 hover:bg-cyan-50 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-cyan-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
          {/* Notifications Section */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-600" />
                Notifications
              </h2>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">Push Notifications</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Get reminders to log meals and track progress
                  </p>
                </div>

                <button
                  onClick={handleNotificationToggle}
                  className={`relative w-16 h-8 rounded-full transition-all duration-300 ${
                    notificationsEnabled
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500'
                      : 'bg-gray-300'
                  }`}
                >
                  <motion.div
                    className="absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center"
                    animate={{ x: notificationsEnabled ? 32 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    {notificationsEnabled ? (
                      <Bell className="w-3 h-3 text-cyan-600" />
                    ) : (
                      <BellOff className="w-3 h-3 text-gray-400" />
                    )}
                  </motion.div>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Account Section */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Account</h2>

              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-red-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 group-hover:bg-red-200 transition-colors">
                    <LogOut className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="font-medium text-red-600">Log Out</span>
                </div>
                <ArrowLeft className="w-5 h-5 text-red-400 rotate-180" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            >
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <LogOut className="w-8 h-8 text-red-600" />
                </div>

                <h3 className="text-2xl font-bold text-gray-800 text-center mb-2">
                  Log Out?
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to log out? You'll need to sign in again to access your account.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLogoutConfirm(false)}
                    className="flex-1 py-3 px-4 rounded-xl font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex-1 py-3 px-4 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    Log Out
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Feedback Toast */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span className="font-medium">{feedbackMessage}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
